import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, Stack, Grid, Chip, Button, Tooltip, IconButton, Collapse } from '@mui/material';
import { keyframes } from '@mui/system';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import BeachAccessRoundedIcon from '@mui/icons-material/BeachAccessRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import BuildCircleRoundedIcon from '@mui/icons-material/BuildCircleRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveEntity } from '../redux/slices/orgSlice';
import http, { apiErrorMessage } from '../Api/http';
import { GetMasterDashboard } from '../Api/Api';
import Loader from '../components/Loader';
import { brandColor } from '../theme';
import { LoadAlert } from '../components/uiKit';

const PRIMARY = '#135F93';
const PRIMARY_LIGHT = '#E4F0F9';
const INK = '#0F172A';
const card = { bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '12px', boxShadow: '0 1px 3px rgba(16,24,40,0.06)' };

const fadeUp = keyframes`from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; }`;
const enter = (i) => ({ animation: `${fadeUp} .5s cubic-bezier(.22,1,.36,1) both`, animationDelay: `${i * 0.05}s` });

const n = (v) => Number(v) || 0;
const num = (v) => n(v).toLocaleString('en-IN');
const compactInr = (v) => {
    const x = n(v); const a = Math.abs(x);
    if (a >= 1e7) return `₹${(x / 1e7).toFixed(a >= 1e8 ? 0 : 1)}Cr`;
    if (a >= 1e5) return `₹${(x / 1e5).toFixed(a >= 1e6 ? 0 : 1)}L`;
    if (a >= 1e3) return `₹${(x / 1e3).toFixed(a >= 1e4 ? 0 : 1)}K`;
    return `₹${Math.round(x)}`;
};
const initials = (s = '') => s.split(' ').filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

const fmtNiceDate = (dmy) => {
    const m = String(dmy || '').match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (!m) return dmy || '';
    const d = new Date(`${m[3]}-${m[2]}-${m[1]}`);
    return Number.isNaN(d.getTime()) ? dmy : d.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
};

const EMP_COLOR = { Permanent: '#135F93', Probation: '#F59E0B', Contract: '#0EA5E9', Internship: '#16A34A', 'Part-Time': '#E11D48' };
const empColor = (k) => EMP_COLOR[k] || '#94A3B8';

const normEntity = (e) => ({
    id: e.entityId,
    name: e.entityName ?? '',
    code: e.shortCode ?? '',
    city: e.city ?? '',
    color: brandColor(e.entityColour),
    active: e.isActive !== false,
    setupReady: e.setup?.ready === true,
    pendingRequired: n(e.setup?.pendingRequired),
    employees: n(e.workforce?.totalEmployees),
    present: n(e.attendanceToday?.present),
    presentPercent: n(e.attendanceToday?.presentPercent),
    marked: n(e.attendanceToday?.marked),
    pendingApprovals: n(e.pendingApprovals),
    payrollBadge: e.payroll?.statusBadge || (e.payroll?.hasCycle ? 'In progress' : 'No cycle'),
    payrollHasCycle: e.payroll?.hasCycle === true,
    docsMissing: (Array.isArray(e.documents?.missingByType) ? e.documents.missingByType : []).reduce((s, g) => s + n(g.count), 0),
});

// One white card: icon tile + title (+ muted subtitle) on the left, optional link on the right.
function Panel({ icon: Icon, title, hint, action, onAction, children, sx, i = 0 }) {
    return (
        <Box sx={{ ...card, borderRadius: '10px', p: 2, height: '100%', display: 'flex', flexDirection: 'column', ...enter(i), ...sx }}>
            {(title || action) && (
                <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.6 }}>
                    <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', minWidth: 0 }}>
                        {Icon && (
                            <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY}26`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Icon sx={{ fontSize: 19, color: PRIMARY }} />
                            </Box>
                        )}
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 800, color: INK, lineHeight: 1.2 }}>{title}</Typography>
                            {hint && <Typography sx={{ fontSize: 11.5, color: '#94A3B8', mt: 0.2 }}>{hint}</Typography>}
                        </Box>
                    </Stack>
                    {action && (
                        <Button onClick={onAction} endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />} sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 700, color: PRIMARY, borderRadius: '7px', px: 1.2, flexShrink: 0, '&:hover': { bgcolor: PRIMARY_LIGHT } }}>{action}</Button>
                    )}
                </Stack>
            )}
            {children}
        </Box>
    );
}

// Blue "View … →" link pinned to the bottom of a card.
function FootLink({ label, onClick }) {
    return (
        <Box sx={{ mt: 'auto', pt: 1.6 }}>
            <Button onClick={onClick} endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />} sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: 13, fontWeight: 700, color: PRIMARY, '&:hover': { bgcolor: 'transparent', opacity: 0.8 } }}>{label}</Button>
        </Box>
    );
}

// The group-wide missing-documents breakdown: one row per required document
// type, expanding into the employees missing it across every entity.
function GroupMissingDocumentsCard({ groups, onOpenEmployee }) {
    const [openKey, setOpenKey] = useState(null);

    if (!groups.length) {
        return (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'center', py: 2 }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 18, color: '#16A34A' }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#16A34A' }}>No missing KYC documents across the group.</Typography>
            </Stack>
        );
    }

    return (
        <Stack spacing={0.8}>
            {groups.map((g) => {
                const open = openKey === g.documentType;
                const employees = Array.isArray(g.employees) ? g.employees : [];
                return (
                    <Box key={g.documentType} sx={{ border: '1px solid #EEF0F6', borderRadius: '9px', overflow: 'hidden' }}>
                        <Stack
                            direction="row" onClick={() => setOpenKey(open ? null : g.documentType)}
                            sx={{ alignItems: 'center', gap: 1.3, p: 1.2, cursor: 'pointer', '&:hover': { bgcolor: '#F8FAFC' } }}
                        >
                            <Box sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: '#E4F0F9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <DescriptionRoundedIcon sx={{ fontSize: 17, color: '#135F93' }} />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK }} noWrap>{g.label}</Typography>
                                <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{g.count} missing across the group</Typography>
                            </Box>
                            <ExpandMoreRoundedIcon sx={{ fontSize: 20, color: '#98A0AE', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                        </Stack>
                        <Collapse in={open}>
                            <Stack sx={{ borderTop: '1px solid #EEF0F6' }}>
                                {employees.map((e, idx) => (
                                    <Stack
                                        key={`${e.entityId ?? ''}-${e.id ?? idx}`} direction="row"
                                        onClick={() => e.id != null && onOpenEmployee(e)}
                                        sx={{ alignItems: 'center', justifyContent: 'space-between', px: 1.6, py: 1, cursor: e.id != null ? 'pointer' : 'default', '&:hover': e.id != null ? { bgcolor: '#F8FAFC' } : undefined, borderTop: idx === 0 ? 'none' : '1px solid #F7F7FB' }}
                                    >
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#334155' }} noWrap>{e.name}</Typography>
                                            <Typography sx={{ fontSize: 11, color: '#98A0AE' }} noWrap>{e.employeeCode}{e.entityName ? ` · ${e.entityName}` : ''}</Typography>
                                        </Box>
                                        {e.id != null && <ArrowForwardRoundedIcon sx={{ fontSize: 14, color: '#C4CBD6', flexShrink: 0 }} />}
                                    </Stack>
                                ))}
                            </Stack>
                        </Collapse>
                    </Box>
                );
            })}
        </Stack>
    );
}

export default function MasterDashboardPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userName = useSelector((s) => s.auth.userName);
    const organisation = useSelector((s) => s.auth.organisation);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetMasterDashboard);
            if (body?.error) throw new Error(body.message || 'Could not load the master dashboard.');
            setData(body?.data || null);
            setLoadError('');
        } catch (err) {
            setLoadError(apiErrorMessage(err, 'Could not load the master dashboard.'));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const t = data?.totals || {};
    const wf = t.workforce || {};
    const att = t.attendanceToday || {};
    const pay = t.payroll || {};
    const rec = t.recruitment || {};

    const entities = useMemo(
        () => (Array.isArray(data?.entities) ? data.entities : [])
            .map(normEntity)
            .sort((a, b) => (a.active === b.active ? b.employees - a.employees : (a.active ? -1 : 1))),
        [data],
    );

    const empTypes = useMemo(() => Object.entries(wf.byEmploymentType || {}).map(([k, v]) => ({ k, v: n(v) })), [wf.byEmploymentType]);
    const empTotal = empTypes.reduce((s, x) => s + x.v, 0) || 1;

    const openEntity = (e) => { if (!e.active) return; dispatch(setActiveEntity(e.id)); navigate('/dashboard'); };
    const missingDocGroups = Array.isArray(t.documents?.missingByType) ? t.documents.missingByType : [];
    const openMissingDocEmployee = (emp) => {
        if (emp.entityId != null) dispatch(setActiveEntity(emp.entityId));
        navigate(`/dashboard/employees/${emp.id}`);
    };

    if (loading) return <Loader />;
    if (loadError) {
        return (
            <Box sx={{ p: 2 }}>
                <LoadAlert sx={{ borderRadius: '9px' }} action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>{loadError}</LoadAlert>
            </Box>
        );
    }

    const heroStats = [
        { label: 'Entities', value: num(t.entities), sub: `${n(t.activeEntities)} active · ${n(t.inactiveEntities)} inactive`, icon: ApartmentRoundedIcon, color: PRIMARY, bg: PRIMARY_LIGHT },
        { label: 'Workforce', value: num(wf.totalEmployees), sub: `${n(wf.active)} active`, icon: GroupsRoundedIcon, color: PRIMARY, bg: PRIMARY_LIGHT },
        { label: 'Present today', value: num(att.present), sub: `${n(att.presentPercent)}% · ${n(att.marked)} marked`, icon: HowToRegRoundedIcon, color: '#16A34A', bg: '#E8F7EE' },
        { label: 'Monthly payout', value: compactInr(pay.grossPayout), sub: pay.hasCycle ? (pay.statusBadge || 'in progress') : 'no cycle', icon: PaymentsRoundedIcon, color: PRIMARY, bg: PRIMARY_LIGHT },
    ];

    const attention = [
        { key: 'documents', label: 'Document approvals', value: n(t.documents?.pendingApprovals), icon: DescriptionRoundedIcon, color: '#0EA5E9', to: '/dashboard/documents?tab=approvals' },
        { key: 'leave', label: 'Leave requests', value: n(t.leave?.pendingRequests), icon: BeachAccessRoundedIcon, color: '#0891B2', to: '/dashboard/leave-management' },
        { key: 'overtime', label: 'Overtime requests', value: n(t.overtime?.pendingRequests), icon: MoreTimeRoundedIcon, color: '#F59E0B', to: '/dashboard/pay-adjustments?tab=overtime' },
        { key: 'advances', label: 'Advance requests', value: n(t.advances?.pendingRequests), icon: SavingsRoundedIcon, color: '#16A34A', to: '/dashboard/pay-adjustments?tab=advances' },
        { key: 'setup', label: 'Entities need setup', value: n(t.entitiesNeedingSetup), icon: BuildCircleRoundedIcon, color: '#E11D48', to: '/dashboard/entities' },
        { key: 'probation', label: 'Probation ending', value: n(wf.probationEndingSoon), icon: HowToRegRoundedIcon, color: '#135F93', to: '/dashboard/employees' },
        { key: 'resignations', label: 'Resignations pending', value: n(t.resignations?.pending), icon: LogoutRoundedIcon, color: '#DC2626', to: '/dashboard/employees' },
    ].filter((x) => x.value > 0);
    const attentionTotal = attention.reduce((s, x) => s + n(x.value), 0);

    const attRows = [
        { label: 'Present', value: n(att.present), color: '#16A34A' },
        { label: 'Absent', value: n(att.absent), color: '#F59E0B' },
        { label: 'On leave', value: n(att.onLeave), color: '#0891B2' },
        { label: 'Late', value: n(att.late), color: '#E11D48' },
        { label: 'Not marked', value: n(att.notMarked), color: '#94A3B8' },
    ];

    const pipeline = [
        { label: 'Open vacancies', value: n(rec.openVacancies), icon: WorkRoundedIcon, color: '#135F93', to: '/dashboard/recruitment?tab=vacancies' },
        { label: 'Candidates in pipeline', value: n(rec.candidatesInPipeline), icon: PersonSearchRoundedIcon, color: '#0EA5E9', to: '/dashboard/recruitment?tab=candidates' },
        { label: 'Interviews today', value: n(rec.interviewsToday), icon: EventAvailableRoundedIcon, color: '#16A34A', to: '/dashboard/recruitment?tab=interviews' },
        { label: 'OT hours this month', value: n(t.overtime?.approvedHoursThisMonth), icon: MoreTimeRoundedIcon, color: '#F59E0B', to: '/dashboard/pay-adjustments?tab=overtime' },
        { label: 'Advances outstanding', value: compactInr(t.advances?.outstandingAmount), icon: SavingsRoundedIcon, color: '#0891B2', to: '/dashboard/pay-adjustments?tab=advances', money: true },
    ];

    const orgName = organisation || entities[0]?.name || '';

    return (
        <Box sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ ...enter(0), p: { xs: 2, md: 2.6 }, borderRadius: '14px', bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY}26` }}>
                <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
                        <AdminPanelSettingsRoundedIcon sx={{ fontSize: 15, color: PRIMARY }} />
                        <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.7px', color: PRIMARY }}>MASTER CONTROL</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Tooltip arrow title="Reload">
                            <IconButton onClick={load} size="small" sx={{ color: '#64748B', '&:hover': { bgcolor: '#fff', color: PRIMARY } }}>
                                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        <Button onClick={() => navigate('/dashboard/entities')} startIcon={<ApartmentRoundedIcon />} sx={{ height: 40, px: 2, textTransform: 'none', fontWeight: 700, borderRadius: '8px', color: '#fff', bgcolor: PRIMARY, boxShadow: `0 2px 6px ${PRIMARY}40`, '&:hover': { bgcolor: '#0F4F7B' } }}>Manage Entities</Button>
                    </Stack>
                </Stack>
                <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, color: INK, letterSpacing: '-0.3px', lineHeight: 1.15, mt: 1.4 }}>Group Command Center</Typography>
                <Typography sx={{ fontSize: 13.5, color: '#64748B', mt: 0.8 }}>
                    Every business entity in one view{userName ? ` · ${userName}` : ''}
                </Typography>
                <Box sx={{ borderTop: `1px solid ${PRIMARY}26`, mt: 2, pt: 1.4 }}>
                    <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5, color: '#64748B' }}>
                        {orgName && (
                            <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center' }}>
                                <ApartmentRoundedIcon sx={{ fontSize: 15, color: PRIMARY }} />
                                <Typography sx={{ fontSize: 12.5 }}>{orgName}</Typography>
                            </Stack>
                        )}
                        <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center' }}>
                            <CalendarMonthRoundedIcon sx={{ fontSize: 15, color: PRIMARY }} />
                            <Typography sx={{ fontSize: 12.5 }}>{fmtNiceDate(data?.generatedOn)}</Typography>
                        </Stack>
                    </Stack>
                </Box>
            </Box>

            {/* Stats — four equal tiles in one row */}
            <Grid container spacing={1.25} sx={{ mt: 2 }}>
                {heroStats.map((s, i) => (
                    <Grid size={{ xs: 6, md: 3 }} key={s.label}>
                        <Box sx={{ ...enter(i + 1), height: '100%', p: 2, borderRadius: '10px', bgcolor: s.bg, border: `1px solid ${s.color}26` }}>
                            <s.icon sx={{ fontSize: 18, color: s.color }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.4px', color: s.color, textTransform: 'uppercase', mt: 0.8 }}>{s.label}</Typography>
                            <Typography sx={{ fontSize: 26, fontWeight: 800, color: INK, lineHeight: 1.15, mt: 0.4 }}>{s.value}</Typography>
                            <Typography sx={{ fontSize: 11.5, color: '#64748B', mt: 0.3 }}>{s.sub}</Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {/* Attendance + Payroll */}
            <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Panel icon={HowToRegRoundedIcon} title="Attendance · Today" i={3}>
                        <Typography sx={{ fontSize: 32, fontWeight: 800, color: PRIMARY, lineHeight: 1 }}>{n(att.presentPercent)}%</Typography>
                        <Typography sx={{ fontSize: 12.5, color: '#64748B', mt: 0.6 }}>Present · {n(att.marked)} of {n(wf.active)} marked</Typography>
                        <Box sx={{ borderTop: '1px solid #EEF0F6', mt: 1.6, pt: 1.2 }}>
                            <Stack spacing={1.1}>
                                {attRows.map((r) => (
                                    <Stack key={r.label} direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.color }} />
                                            <Typography sx={{ fontSize: 12.5, color: '#475569' }}>{r.label}</Typography>
                                        </Stack>
                                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: INK }}>{num(r.value)}</Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                        <FootLink label="View Attendance" onClick={() => navigate('/dashboard/attendance-leave?tab=overview')} />
                    </Panel>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Panel icon={PaymentsRoundedIcon} title="Payroll" i={4}>
                        <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>{pay.latestMonth || 'Current period'}</Typography>
                        <Chip label={pay.statusBadge || (pay.hasCycle ? 'In progress' : 'No cycle')} size="small" sx={{ alignSelf: 'flex-start', height: 22, fontSize: 11, fontWeight: 800, mt: 0.8, mb: 1.6, bgcolor: pay.hasCycle ? '#DCFCE7' : '#F1F5F9', color: pay.hasCycle ? '#16A34A' : '#64748B' }} />
                        <Stack direction="row" spacing={1.2} sx={{ mb: 1.4 }}>
                            {[['Gross', compactInr(pay.grossPayout)], ['Net', compactInr(pay.netPayout)]].map(([l, v]) => (
                                <Box key={l} sx={{ flex: 1, p: 1.4, borderRadius: '10px', bgcolor: '#F8FAFC', border: '1px solid #EEF0F6' }}>
                                    <Typography sx={{ fontSize: 9.5, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{l}</Typography>
                                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: INK, mt: 0.3 }}>{v}</Typography>
                                </Box>
                            ))}
                        </Stack>
                        <Typography sx={{ fontSize: 12, color: '#64748B' }}>
                            <strong style={{ color: INK }}>{num(pay.employeesInCycle)}</strong> employees in cycle · <strong style={{ color: INK }}>{num(pay.cyclesInProgress)}</strong> cycle(s) running
                        </Typography>
                        <FootLink label="Run Payroll" onClick={() => navigate('/dashboard/run-payroll?tab=register')} />
                    </Panel>
                </Grid>
            </Grid>

            {/* Workforce + Waiting on you */}
            <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Panel icon={GroupsRoundedIcon} title="Workforce" hint={`New this month: ${n(wf.newJoinersThisMonth)}`} i={5}>
                        <Typography sx={{ fontSize: 32, fontWeight: 800, color: INK, lineHeight: 1 }}>{num(wf.totalEmployees)}</Typography>
                        <Typography sx={{ fontSize: 12.5, color: '#64748B', mt: 0.6 }}>{n(wf.active)} active employees</Typography>
                        <Box sx={{ borderTop: '1px solid #EEF0F6', mt: 1.6, pt: 1.4 }}>
                            <Stack spacing={1.2}>
                                <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: 12.5, color: '#475569' }}>Inactive</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: INK }}>{num(wf.inactive)}</Typography>
                                </Stack>
                                {empTypes.map((et) => (
                                    <Box key={et.k}>
                                        <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.4 }}>
                                            <Typography sx={{ fontSize: 12.5, color: '#475569' }}>{et.k}</Typography>
                                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: INK }}>{et.v}</Typography>
                                        </Stack>
                                        <Box sx={{ height: 6, borderRadius: '4px', bgcolor: '#F1F5F9', overflow: 'hidden' }}>
                                            <Box sx={{ height: '100%', width: `${(et.v / empTotal) * 100}%`, bgcolor: empColor(et.k), borderRadius: '4px' }} />
                                        </Box>
                                    </Box>
                                ))}
                                {empTypes.length === 0 && <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>No employees yet.</Typography>}
                            </Stack>
                        </Box>
                        <FootLink label="Employees" onClick={() => navigate('/dashboard/employees')} />
                    </Panel>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Panel icon={FactCheckRoundedIcon} title="Waiting on You" hint={`${num(attentionTotal)} action(s) · ${num(t.pendingApprovals)} approval(s)`} i={6}>
                        {attention.length > 0 ? (
                            <Stack spacing={0.8}>
                                {attention.map((c) => (
                                    <Stack
                                        key={c.key} direction="row" onClick={() => navigate(c.to)}
                                        sx={{ alignItems: 'center', gap: 1.3, p: 1.1, cursor: 'pointer', borderRadius: '9px', border: '1px solid #EEF0F6', '&:hover': { bgcolor: '#F8FAFC' } }}
                                    >
                                        <Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: `${c.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <c.icon sx={{ fontSize: 17, color: c.color }} />
                                        </Box>
                                        <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: '#334155' }} noWrap>{c.label}</Typography>
                                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: INK }}>{num(c.value)}</Typography>
                                        <ArrowForwardRoundedIcon sx={{ fontSize: 15, color: '#C4CBD6', flexShrink: 0 }} />
                                    </Stack>
                                ))}
                            </Stack>
                        ) : (
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'center', py: 2 }}>
                                <CheckCircleRoundedIcon sx={{ fontSize: 20, color: '#16A34A' }} />
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#16A34A' }}>All clear across the group — nothing pending right now.</Typography>
                            </Stack>
                        )}
                        {n(t.entitiesNeedingSetup) > 0 && <FootLink label="Review setup" onClick={() => navigate('/dashboard/entities')} />}
                    </Panel>
                </Grid>
            </Grid>

            {/* Documents + charts (left) | Business Entities (right) */}
            <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Stack spacing={1.5} sx={{ height: '100%' }}>
                        <Panel icon={DescriptionRoundedIcon} title="Missing Documents" hint="Active employees missing a required KYC document, across every entity" i={7} sx={{ height: 'auto' }}>
                            <GroupMissingDocumentsCard groups={missingDocGroups} onOpenEmployee={openMissingDocEmployee} />
                        </Panel>
                        <Panel icon={GroupsRoundedIcon} title="Headcount by Entity" hint="Employees per company" i={8} sx={{ height: 'auto' }}>
                            {entities.some((e) => e.employees > 0) ? (
                                <Stack spacing={1.2}>
                                    {entities.map((e) => (
                                        <Box key={e.id} sx={{ overflow: 'hidden', borderRadius: '10px', bgcolor: '#EDF6FD', border: '1px solid #C2DCEE' }}>
                                            <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center', p: 1.3 }}>
                                                <Box sx={{ width: 26, alignSelf: 'stretch', minHeight: 34, borderRadius: '4px', bgcolor: e.color, opacity: 0.35 + 0.65 * (e.employees / Math.max(1, ...entities.map((x) => x.employees))), flexShrink: 0 }} />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK }} noWrap>{e.code || e.name}</Typography>
                                                    <Typography sx={{ fontSize: 11.5, color: '#64748B' }}>{num(e.employees)} employee{e.employees === 1 ? '' : 's'}</Typography>
                                                </Box>
                                            </Stack>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography sx={{ fontSize: 12.5, color: '#94A3B8', textAlign: 'center', py: 6 }}>No employees on record yet.</Typography>
                            )}
                        </Panel>
                        <Panel icon={FactCheckRoundedIcon} title="Pending Approvals by Entity" hint="Where the work is piling up" i={9} sx={{ height: 'auto' }}>
                            {entities.some((e) => e.pendingApprovals > 0) ? (
                                <Stack spacing={1.2}>
                                    {entities.filter((e) => e.pendingApprovals > 0).map((e) => (
                                        <Box key={e.id} sx={{ overflow: 'hidden', borderRadius: '10px', bgcolor: '#FFF7ED', border: '1px solid #FCEBD0' }}>
                                            <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center', p: 1.3 }}>
                                                <Box sx={{ width: 26, alignSelf: 'stretch', minHeight: 34, borderRadius: '4px', bgcolor: '#F59E0B', opacity: 0.35 + 0.65 * (e.pendingApprovals / Math.max(1, ...entities.map((x) => x.pendingApprovals))), flexShrink: 0 }} />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK }} noWrap>{e.code || e.name}</Typography>
                                                    <Typography sx={{ fontSize: 11.5, color: '#64748B' }}>{num(e.pendingApprovals)} pending approval{e.pendingApprovals === 1 ? '' : 's'}</Typography>
                                                </Box>
                                            </Stack>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography sx={{ fontSize: 12.5, color: '#94A3B8', textAlign: 'center', py: 6 }}>Nothing pending across the group.</Typography>
                            )}
                        </Panel>
                    </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Panel icon={ApartmentRoundedIcon} title="Business Entities" hint={`${num(t.entities)} companies · ${n(t.activeEntities)} active · ${n(t.entitiesNeedingSetup)} need setup`} action="Manage" onAction={() => navigate('/dashboard/entities')} i={7} sx={{ height: 'auto' }}>
                        {entities.length === 0 ? (
                            <Typography sx={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', py: 2 }}>No entities yet.</Typography>
                        ) : (
                            <Stack spacing={1.4}>
                                {entities.map((e, i) => (
                                    <Box key={e.id} sx={{ ...card, ...enter(i), overflow: 'hidden', display: 'flex', flexDirection: 'column', opacity: e.active ? 1 : 0.72, transition: 'transform .18s, box-shadow .18s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 16px 34px -18px rgba(16,24,40,0.3)' } }}>
                                        <Box sx={{ height: 5, bgcolor: e.color }} />
                                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                            <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center' }}>
                                                <Box sx={{ width: 46, height: 46, borderRadius: '12px', bgcolor: `${e.color}1A`, color: e.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, flexShrink: 0 }}>
                                                    {initials(e.name) || <ApartmentRoundedIcon />}
                                                </Box>
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
                                                        <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: INK }} noWrap>{e.name || 'Unnamed'}</Typography>
                                                        <Chip label={e.active ? 'Active' : 'Inactive'} size="small" sx={{ height: 18, fontSize: 9.5, fontWeight: 800, bgcolor: e.active ? '#DCFCE7' : '#F1F5F9', color: e.active ? '#16A34A' : '#64748B' }} />
                                                    </Stack>
                                                    <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }} noWrap>{e.code}{e.city ? ` · ${e.city}` : ''}</Typography>
                                                </Box>
                                            </Stack>

                                            <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
                                                <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center', minWidth: 0 }}>
                                                    {e.setupReady ? (
                                                        <><CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#16A34A' }} /><Typography sx={{ fontSize: 11.5, color: '#16A34A', fontWeight: 700 }} noWrap>Setup complete</Typography></>
                                                    ) : (
                                                        <><BuildCircleRoundedIcon sx={{ fontSize: 16, color: '#E11D48' }} /><Typography sx={{ fontSize: 11.5, color: '#E11D48', fontWeight: 700 }} noWrap>{e.pendingRequired} step(s) pending</Typography></>
                                                    )}
                                                </Stack>
                                                <Chip label={e.payrollBadge} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 800, flexShrink: 0, bgcolor: e.payrollHasCycle ? '#E4F0F9' : '#F1F5F9', color: e.payrollHasCycle ? '#0E4A75' : '#94A3B8' }} />
                                            </Stack>

                                            <Stack direction="row" spacing={1} sx={{ mt: 1.6 }}>
                                                {[['Staff', num(e.employees)], ['Present', num(e.present)], ['Pending', num(e.pendingApprovals)]].map(([l, v]) => (
                                                    <Box key={l} sx={{ flex: 1, textAlign: 'center', py: 1, borderRadius: '9px', bgcolor: '#F8FAFC', border: '1px solid #EEF0F6' }}>
                                                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: INK, lineHeight: 1 }}>{v}</Typography>
                                                        <Typography sx={{ fontSize: 9.5, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, mt: 0.4 }}>{l}</Typography>
                                                    </Box>
                                                ))}
                                            </Stack>

                                            {e.docsMissing > 0 && (
                                                <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center', mt: 1.2 }}>
                                                    <DescriptionRoundedIcon sx={{ fontSize: 14, color: '#F59E0B' }} />
                                                    <Typography sx={{ fontSize: 11, color: '#B45309', fontWeight: 700 }}>{e.docsMissing} missing KYC document(s)</Typography>
                                                </Stack>
                                            )}

                                            <Box sx={{ flexGrow: 1 }} />
                                            <Button
                                                fullWidth onClick={() => openEntity(e)} disabled={!e.active}
                                                endIcon={<LoginRoundedIcon sx={{ fontSize: 16 }} />}
                                                sx={{ mt: 1.8, height: 38, textTransform: 'none', fontWeight: 700, fontSize: 12.5, borderRadius: '9px', color: '#fff', bgcolor: e.color, boxShadow: 'none', '&:hover': { bgcolor: e.color, filter: 'brightness(0.92)' }, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' } }}
                                            >
                                                {e.active ? 'Enter workspace' : 'Inactive'}
                                            </Button>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Panel>
                </Grid>
            </Grid>

            {/* Recruitment & Finance */}
            <Box sx={{ mt: 1.5 }}>
                <Panel icon={WorkRoundedIcon} title="Recruitment & Finance" hint="Across every entity" i={10}>
                    <Grid container spacing={1.25}>
                        {pipeline.map((p, i) => (
                            <Grid size={{ xs: 6, sm: 4, md: 12 / 5 }} key={p.label}>
                                <Box onClick={() => navigate(p.to)} sx={{ ...card, ...enter(i), height: '100%', p: 1.8, cursor: 'pointer', transition: 'transform .18s, box-shadow .18s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 26px -14px rgba(16,24,40,0.28)' } }}>
                                    <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: `${p.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                                        <p.icon sx={{ fontSize: 19, color: p.color }} />
                                    </Box>
                                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: INK, lineHeight: 1 }}>{p.money ? p.value : num(p.value)}</Typography>
                                    <Typography sx={{ fontSize: 11.5, color: '#64748B', fontWeight: 600, mt: 0.3 }} noWrap>{p.label}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Panel>
            </Box>
        </Box>
    );
}
